import React from 'react';
import autoBind from 'react-autobind';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Subscribe, appContainer } from '../../contexts/app';
import {
  BuildingRecord,
  FloorRecord,
  MarkerRecord
} from '../../feathers/records';
import { pointToBounds } from '../../lib/google-maps';
import EditBuilding from './EditBuilding';
import EditFacility from './EditFacility';
import EditFloor from './EditFloor';
import EditMarker from './EditMarker';
import InfographicEditor from './InfographicEditor';
import NewBuilding from './NewBuilding';
import NewFloor from './NewFloor';
import NewMarker from './NewMarker';
import SidebarPage from '../../components/Sidebar/components/SidebarPage';
import SidebarEdit from '../../components/Sidebar/SidebarMap/Edit';
import { MapPortalBase, MapPortal } from '../../components/Map';
import './style.scss';

class Facility extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
  }

  componentDidMount() {
    // Ensure all relative resources are fresh
    this.props.facility.load();
  }

  componentWillReceiveProps(nextProps) {
    // When changing facilities, ensure all
    // relative resources are fresh
    const { facility } = this.props;
    const { facility: nextFacility } = nextProps;
    if (facility._id !== nextFacility._id) {
      nextFacility.load();
    }
  }

  handleRedirect(props) {
    // We need to ensure id's exist at each path endpoint
    // If not, this is either an invalid id or it has been deleted
    const { path, params } = props.match;
    let redirect = null;
    if (
      path === '/facilities/:facilityId/buildings/:buildingId' &&
      !props.buildings.has(params.buildingId)
    ) {
      redirect = `/facilities/${params.facilityId}/buildings`;
    }
    if (
      path ===
        '/facilities/:facilityId/buildings/:buildingId/floors/:floorId' &&
      !props.floors.has(params.floorId)
    ) {
      redirect = `/facilities/${params.facilityId}/buildings/${
        params.buildingId
      }/floors`;
    }
    if (
      path ===
        '/facilities/:facilityId/buildings/:buildingId/floors/:floorId/markers/:markerId' &&
      !props.markers.has(params.markerId)
    ) {
      redirect = `/facilities/${params.facilityId}/buildings/${
        params.buildingId
      }/floors/${params.floorId}/markers`;
    }
    if (
      path ===
        '/facilities/:facilityId/buildings/:buildingId/floors/:floorId/markers/:markerId/infographics/:infographicId' &&
      !props.infographics.has(params.infographicId)
    ) {
      redirect = `/facilities/${params.facilityId}/buildings/${
        params.buildingId
      }/floors/${params.floorId}/markers/${params.markerId}/infographics`;
    }
    return redirect;
  }

  render() {
    const redirect = this.handleRedirect(this.props);
    if (redirect) {
      return <Redirect to={redirect} />;
    }
    const { facility, buildings, floors, markers } = this.props;
    return (
      <div id="facilityPage" className="authed-page">
        <SidebarEdit edit={true} facility={facility} match={this.props.match} />
        <SidebarPage>
          <Switch>
            <Route
              exact
              path="/facilities/:facilityId"
              render={routeProps => (
                <EditFacility
                  {...routeProps}
                  facility={facility}
                  key={facility._id}
                />
              )}
            />
            <Route
              exact
              path="/facilities/:facilityId/buildings"
              render={routeProps => {
                const newBuilding = new BuildingRecord({
                  geo: { coordinates: facility.geo.coordinates },
                  facility_id: facility._id
                });
                return <NewBuilding {...routeProps} building={newBuilding} />;
              }}
            />
            <Route
              exact
              path="/facilities/:facilityId/buildings/:buildingId"
              render={routeProps => {
                const { buildingId } = routeProps.match.params;
                const building = buildings.get(buildingId);
                return (
                  <EditBuilding
                    {...routeProps}
                    building={building}
                    key={building._id}
                  />
                );
              }}
            />
            <Route
              exact
              path="/facilities/:facilityId/buildings/:buildingId/floors"
              render={routeProps => {
                const { buildingId } = routeProps.match.params;
                const building = buildings.get(buildingId);
                const newFloor = new FloorRecord({
                  geo: {
                    coordinates: pointToBounds(building.geo.coordinates, 0.05)
                  },
                  building_id: building._id
                });
                return (
                  <NewFloor
                    {...routeProps}
                    floor={newFloor}
                    defaultCenter={building.geo.coordinates}
                  />
                );
              }}
            />
            <Route
              exact
              path="/facilities/:facilityId/buildings/:buildingId/floors/:floorId"
              render={routeProps => {
                const { buildingId, floorId } = routeProps.match.params;
                const building = buildings.get(buildingId);
                const floor = floors.get(floorId);
                return (
                  <EditFloor
                    {...routeProps}
                    facility={facility}
                    floor={floor}
                    defaultCenter={building.geo.coordinates}
                    key={floor._id}
                  />
                );
              }}
            />
            <Route
              exact
              path="/facilities/:facilityId/buildings/:buildingId/floors/:floorId/markers"
              render={routeProps => {
                const { floorId, buildingId } = routeProps.match.params;
                const floor = floors.get(floorId);
                const building = buildings.get(buildingId);
                const newMarker = new MarkerRecord({
                  geo: { coordinates: building.geo.coordinates },
                  floor_id: floor._id
                });
                return (
                  <NewMarker
                    {...routeProps}
                    facility={facility}
                    marker={newMarker}
                    floor={floor}
                    defaultCenter={building.geo.coordinates}
                  />
                );
              }}
            />
            <Route
              exact
              path="/facilities/:facilityId/buildings/:buildingId/floors/:floorId/markers/:markerId"
              render={routeProps => {
                const { floorId, markerId } = routeProps.match.params;
                const floor = floors.get(floorId);
                const marker = markers.get(markerId);
                return (
                  <EditMarker
                    {...routeProps}
                    facility={facility}
                    marker={marker}
                    floor={floor}
                    defaultCenter={marker.geo.coordinates}
                    key={marker._id}
                  />
                );
              }}
            />
            <Route
              exact
              path="/facilities/:facilityId/buildings/:buildingId/floors/:floorId/markers/:markerId/infographics"
              render={routeProps => {
                const { markerId } = routeProps.match.params;
                const marker = markers.get(markerId);
                // Note the MapPortal is not really used here
                // The InfographicEditor does not change anything
                // on the map, but it is present so that if a
                // user deep links to this page on page load, the
                // map will have a center and not error.
                return (
                  <React.Fragment>
                    <InfographicEditor {...routeProps} marker={marker} />
                    <MapPortal center={marker.geo.coordinates} />
                  </React.Fragment>
                );
              }}
            />
          </Switch>
          <MapPortalBase />
        </SidebarPage>
      </div>
    );
  }
}

export default routerProps => {
  return (
    <Subscribe
      to={[appContainer]}
      render={app => {
        const { facilityId } = routerProps.match.params;
        return (
          <Facility
            {...routerProps}
            facility={app.state.getIn(['facilities', facilityId])}
            buildings={app.state.get('buildings')}
            floors={app.state.get('floors')}
            markers={app.state.get('markers')}
          />
        );
      }}
    />
  );
};



// WEBPACK FOOTER //
// ./src/pages/Facility/index.jsx